import { useState } from "react";
import { updateUser } from "api/user";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";
import { MEDICAL_PROFESSIONS } from "enums";
import { useNavigate, useSearchParams } from "react-router-dom";
import ProfessionStep from "sections/onboarding/profession-step";
import TemplateStep from "sections/onboarding/template-step";

// In-flight onboarding progress, so a refresh or a gate-driven resume into
// /setup-profile continues where the user left off. This is UX-only state; the
// source of truth for "is onboarding done" remains the DB `profession` field.
const ONBOARDING_PROGRESS_KEY = "nirvascribe_onboarding_progress";

const readProgress = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(ONBOARDING_PROGRESS_KEY));
    return parsed && (parsed.step === 1 || parsed.step === 2) ? parsed : null;
  } catch (_) {
    return null;
  }
};

const writeProgress = (progress) => {
  try {
    localStorage.setItem(ONBOARDING_PROGRESS_KEY, JSON.stringify(progress));
  } catch (_) {}
};

export const clearOnboardingProgress = () => {
  try {
    localStorage.removeItem(ONBOARDING_PROGRESS_KEY);
  } catch (_) {}
};

const SetupProfile = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const planValue = searchParams.get("plan");

  // Step 2 is only resumable when a profession was already captured in step 1.
  const [step, setStep] = useState(() => {
    const saved = readProgress();
    return saved?.step === 2 && saved?.professionId ? 2 : 1;
  });
  const [professionId, setProfessionId] = useState(
    () => readProgress()?.professionId ?? null
  );

  const updateUserMutation = useMutation(updateUser, {
    onSuccess: async () => {
      clearOnboardingProgress();
      // Refresh the cached profile so the onboarding gate immediately sees the
      // now-set profession and won't bounce the user back to /setup-profile.
      await queryClient.invalidateQueries("userProfile");
      toast.success("Your profile has been set successfully", {
        position: "top-right",
      });
      const planParam = planValue ? `?plan=${planValue}` : "";
      navigate(`/onboarding-audio-check${planParam}`);
    },
    onError: () => {
      toast.error("That didn't save. Try again.", {
        position: "top-right",
      });
    },
  });

  const handleProfessionNext = (id) => {
    setProfessionId(id);
    setStep(2);
    writeProgress({ step: 2, professionId: id });
  };

  const handleBack = () => {
    setStep(1);
    writeProgress({ step: 1, professionId });
  };

  const handleTemplateSubmit = (commonTemplate) => {
    updateUserMutation.mutate({
      profession:
        MEDICAL_PROFESSIONS.find((el) => el.id === professionId)?.name || "",
      commonTemplate,
      commonConditions: "",
    });
  };

  if (step === 1) {
    return <ProfessionStep onNext={handleProfessionNext} />;
  }

  return (
    <TemplateStep
      onSubmit={handleTemplateSubmit}
      onSkip={() => handleTemplateSubmit("3,4")}
      onBack={handleBack}
      isLoading={updateUserMutation.isLoading}
      professionId={professionId}
    />
  );
};

export default SetupProfile;
